import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PipelineFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  jobPositions,
  candidateSources 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value && value !== 'all' && value !== ''
  );

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="font-medium text-foreground">Filters</h3>
          {hasActiveFilters && (
            <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
              Active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={onClearFilters}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Less' : 'More'}
          </Button>
        </div>
      </div>
      {/* Basic Filters - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Select
          label="Job Position"
          options={[
            { value: 'all', label: 'All Positions' },
            ...jobPositions?.map(job => ({
              value: job?.id,
              label: job?.title
            }))
          ]}
          value={filters?.position || 'all'}
          onChange={(value) => handleFilterChange('position', value)}
          searchable
        />

        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={filters?.dateRange || 'all'}
          onChange={(value) => handleFilterChange('dateRange', value)}
        />

        <Input
          label="Search Candidates"
          type="search"
          placeholder="Name, email, or skills..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
        />
      </div>
      {/* Advanced Filters - Expandable */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-border">
          <Select
            label="Source"
            options={[
              { value: 'all', label: 'All Sources' },
              ...candidateSources?.map(source => ({
                value: source?.value,
                label: source?.label
              }))
            ]}
            value={filters?.source || 'all'}
            onChange={(value) => handleFilterChange('source', value)}
          />

          <Select
            label="Experience Level"
            options={[
              { value: 'all', label: 'All Levels' },
              { value: 'entry', label: 'Entry Level (0-2 years)' },
              { value: 'mid', label: 'Mid Level (3-5 years)' },
              { value: 'senior', label: 'Senior Level (6-10 years)' },
              { value: 'lead', label: 'Lead Level (10+ years)' }
            ]}
            value={filters?.experience || 'all'}
            onChange={(value) => handleFilterChange('experience', value)}
          />

          <Select
            label="Score Range"
            options={[
              { value: 'all', label: 'All Scores' },
              { value: 'high', label: 'High (80-100%)' },
              { value: 'medium', label: 'Medium (60-79%)' },
              { value: 'low', label: 'Low (0-59%)' }
            ]}
            value={filters?.score || 'all'}
            onChange={(value) => handleFilterChange('score', value)}
          />

          <Select
            label="Location"
            options={[
              { value: 'all', label: 'All Locations' },
              { value: 'remote', label: 'Remote' },
              { value: 'onsite', label: 'On-site' },
              { value: 'hybrid', label: 'Hybrid' }
            ]}
            value={filters?.location || 'all'}
            onChange={(value) => handleFilterChange('location', value)}
          />
        </div>
      )}
      {/* Custom Date Range */}
      {filters?.dateRange === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
          <Input
            label="Start Date"
            type="date"
            value={filters?.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
          />
          <Input
            label="End Date"
            type="date"
            value={filters?.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
          />
        </div>
      )}
    </div>
  );
};

export default PipelineFilters;